/**
 * 二级Tab组件
 * 支持一级Tab和二级Tab，有二级Tab时点击一级Tab切换下属二级Tab
 * 点击二级Tab切换内容，没有二级Tab直接切换内容
 * 集成Swiper实现PC端按钮左右滚动，移动端左右滑动
 */

(function() {
  'use strict';

  // 检测是否为移动端
  const isMobile = () => window.innerWidth < 768;

  // ========================
  // Tab列表容器 - 集成Swiper
  // ========================
  class TabListT extends HTMLElement {
    constructor() {
      super();
      this.swiperInstance = null;
    }

    connectedCallback() {
      this.setAttribute('role', 'tablist');
      
      // 使用 requestAnimationFrame 确保 DOM 完全渲染
      requestAnimationFrame(() => {
        this._setupSwiperStructure();
        this._initSwiper();
        this._updateIndicator();
      });
    }

    disconnectedCallback() {
      this._destroySwiper();
    }

    // 创建Swiper结构
    _setupSwiperStructure() {
      // 检查是否已经包装过
      if (this.querySelector('.swiper-wrapper')) return;

      // 添加swiper类
      this.classList.add('swiper');

      // 创建swiper-wrapper
      const wrapper = document.createElement('div');
      wrapper.className = 'swiper-wrapper';
      
      // 将所有tab-item包装成slide
      const items = Array.from(this.querySelectorAll(':scope > tab-item'));
      items.forEach(item => {
        const slide = document.createElement('div');
        slide.className = 'swiper-slide';
        slide.appendChild(item);
        wrapper.appendChild(slide);
      });
      
      // 指示器放在wrapper内部，跟随滚动
      const indicator = document.createElement('div');
      indicator.className = 'tab-indicator';
      wrapper.appendChild(indicator);
      this._indicator = indicator;
      
      this.insertBefore(wrapper, this.firstChild);

      // 创建下划线（固定在底部）
      const underline = document.createElement('div');
      underline.className = 'tab-underline';
      this.appendChild(underline);

      // 创建导航按钮
      const prevBtn = document.createElement('div');
      prevBtn.className = 'swiper-button-prev-1 tab-swiper-prev';
      prevBtn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="9" height="15" viewBox="0 0 9 15" fill="none">
<path d="M7.56055 0.75L1.06055 7.25L7.56055 13.75" stroke="black" stroke-opacity="0.4" stroke-width="1.5" stroke-linecap="round"/>
</svg>`;
      
      const nextBtn = document.createElement('div');
      nextBtn.className = 'swiper-button-next-1 tab-swiper-next';
      nextBtn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="9" height="15" viewBox="0 0 9 15" fill="none">
<path d="M0.75 0.75L7.25 7.25L0.75 13.75" stroke="black" stroke-opacity="0.4" stroke-width="1.5" stroke-linecap="round"/>
</svg>`;
      
      this.appendChild(prevBtn);
      this.appendChild(nextBtn);

      this._prevBtn = prevBtn;
      this._nextBtn = nextBtn;
      this._wrapper = wrapper;
    }

    // 初始化Swiper
    _initSwiper() {
      if (typeof Swiper === 'undefined') {
        console.warn('Swiper is not loaded, falling back to native scroll');
        this._setupNativeScroll();
        return;
      }

      const self = this;

      this.swiperInstance = new Swiper(this, {
        slidesPerView: 'auto',
        spaceBetween: 0,
        initialSlide: 0,
        centeredSlides: false,
        freeMode: true,
        grabCursor: true,
        watchSlidesProgress: true,
        preventClicks: false,
        preventClicksPropagation: false,
        touchStartPreventDefault: false,
        navigation: {
          nextEl: this._nextBtn,
          prevEl: this._prevBtn,
        },
        on: {
          init: function() {
            self._updateNavVisibility();
            setTimeout(() => {
              self._updateIndicator();
            }, 50);
          },
          slideChange: function() {
            self._updateNavVisibility();
          },
          progress: function() {
            self._updateNavVisibility();
          },
          resize: function() {
            self._updateNavVisibility();
            self._updateIndicator();
          }
        }
      });

      // 窗口resize时更新
      this._onResize = () => {
        if (this.swiperInstance) {
          this.swiperInstance.update();
          this._updateNavVisibility();
          this._updateIndicator();
        }
      };
      window.addEventListener('resize', this._onResize);
    }

    // 原生滚动回退方案
    _setupNativeScroll() {
      const wrapper = this.querySelector('.swiper-wrapper');
      if (wrapper) {
        wrapper.style.overflowX = 'auto';
        wrapper.style.display = 'flex';
        wrapper.style.scrollSnapType = 'x proximity';
        wrapper.style.scrollbarWidth = 'none';
        wrapper.style.msOverflowStyle = 'none';
      }
    }

    // 销毁Swiper
    _destroySwiper() {
      if (this.swiperInstance) {
        this.swiperInstance.destroy(true, true);
        this.swiperInstance = null;
      }
      if (this._onResize) {
        window.removeEventListener('resize', this._onResize);
      }
    }

    // 更新导航按钮可见性
    _updateNavVisibility() {
      if (!this.swiperInstance) return;

      const { isBeginning, isEnd, isLocked } = this.swiperInstance;

      if (this._prevBtn) {
        this._prevBtn.classList.toggle('swiper-button-disabled', isBeginning);
        this._prevBtn.classList.toggle('swiper-button-hidden', isLocked);
      }
      if (this._nextBtn) {
        this._nextBtn.classList.toggle('swiper-button-disabled', isEnd);
        this._nextBtn.classList.toggle('swiper-button-hidden', isLocked);
      }
    }

    // 更新指示器位置
    updateIndicator() {
      this._updateIndicator();
    }

    _updateIndicator() {
      const activeTab = this.querySelector('tab-item[active]');
      const indicator = this._indicator || this.querySelector('.tab-indicator');
      if (!activeTab || !indicator) return;

      try {
        // 计算相对于wrapper的位置（使用offsetLeft，不受滚动影响）
        const slide = activeTab.closest('.swiper-slide');
        if (!slide) return;
        
        const left = slide.offsetLeft;
        const width = activeTab.offsetWidth;

        indicator.style.width = `${width}px`;
        indicator.style.transform = `translateX(${left}px)`;
      } catch (error) {
        console.warn('指示器位置计算失败:', error);
      }
    }

    // 滚动到激活的tab
    scrollToActive() {
      const activeTab = this.querySelector('tab-item[active]');
      if (!activeTab || !this.swiperInstance) return;

      const slide = activeTab.closest('.swiper-slide');
      if (slide) {
        const slideIndex = Array.from(this.querySelectorAll('.swiper-slide')).indexOf(slide);
        if (slideIndex !== -1) {
          this.swiperInstance.slideTo(slideIndex, 300);
        }
      }
    }

    // 滚动到第一个位置
    scrollToStart() {
      if (this.swiperInstance) {
        this.swiperInstance.slideTo(0, 300);
      }
    }
  }

  // ========================
  // Tab项
  // ========================
  class TabItemT extends HTMLElement {
    static get observedAttributes() {
      return ['active'];
    }

    constructor() {
      super();
      this._active = false;
      this._preventClick = false;
    }

    connectedCallback() {
      if (!this.hasAttribute('role')) {
        this.setAttribute('role', 'tab');
      }
      this.setAttribute('tabindex', this.hasAttribute('active') ? '0' : '-1');

      this._onClick = this._handleClick.bind(this);
      this._onKeydown = this._handleKeydown.bind(this);
      this._onTouchStart = this._handleTouchStart.bind(this);
      this._onTouchMove = this._handleTouchMove.bind(this);
      
      this.addEventListener('click', this._onClick);
      this.addEventListener('keydown', this._onKeydown);
      this.addEventListener('touchstart', this._onTouchStart, { passive: true });
      this.addEventListener('touchmove', this._onTouchMove, { passive: true });
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
      this.removeEventListener('keydown', this._onKeydown);
      this.removeEventListener('touchstart', this._onTouchStart);
      this.removeEventListener('touchmove', this._onTouchMove);
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (name === 'active') {
        this._active = this.hasAttribute('active');
        this.setAttribute('aria-selected', this._active.toString());
        this.setAttribute('tabindex', this._active ? '0' : '-1');

        if (this._active) {
          const list = this.closest('tab-list');
          if (list) {
            requestAnimationFrame(() => {
              list.updateIndicator();
            });
          }
        }
      }
    }

    _handleTouchStart(e) {
      this._touchStartX = e.touches[0].clientX;
      this._touchStartY = e.touches[0].clientY;
      this._preventClick = false;
    }

    _handleTouchMove(e) {
      if (!this._touchStartX) return;
      const deltaX = Math.abs(e.touches[0].clientX - this._touchStartX);
      const deltaY = Math.abs(e.touches[0].clientY - this._touchStartY);
      // 如果有明显的滑动，阻止点击
      if (deltaX > 10 || deltaY > 10) {
        this._preventClick = true;
      }
    }

    _handleClick(e) {
      // 如果正在滑动，不触发点击
      if (this._preventClick) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      this.select();
    }

    _handleKeydown(event) {
      switch (event.key) {
        case 'Enter':
        case ' ':
          event.preventDefault();
          this.select();
          break;
        case 'ArrowLeft':
          event.preventDefault();
          this._selectSibling(-1);
          break;
        case 'ArrowRight':
          event.preventDefault();
          this._selectSibling(1);
          break;
      }
    }

    select() {
      if (this._active) return;

      const list = this.closest('tab-list');
      if (!list) return;

      // 取消同级其他tab的选中状态
      const siblings = list.querySelectorAll('tab-item');
      siblings.forEach(tab => {
        if (tab !== this) {
          tab.removeAttribute('active');
        }
      });

      // 选中当前tab
      this.setAttribute('active', '');

      // 触发选中事件
      const level = list.getAttribute('level') || 'one';
      this.dispatchEvent(new CustomEvent('tab:select', {
        bubbles: true,
        detail: {
          tabId: this.dataset.tabId,
          level: level,
          parentId: level === 'two' ? list.dataset.parent : null
        }
      }));
    }

    _selectSibling(direction) {
      const list = this.closest('tab-list');
      if (!list) return;

      const tabs = Array.from(list.querySelectorAll('tab-item'));
      const currentIndex = tabs.indexOf(this);
      let newIndex = currentIndex + direction;
      
      // 循环
      if (newIndex < 0) newIndex = tabs.length - 1;
      if (newIndex >= tabs.length) newIndex = 0;
      
      tabs[newIndex]?.select();
    }
  }

  // ========================
  // Tab面板
  // ========================
  class TabPanelT extends HTMLElement {
    connectedCallback() {
      if (!this.hasAttribute('role')) {
        this.setAttribute('role', 'tabpanel');
      }
    }
  }

  // ========================
  // 主Tab组件
  // ========================
  class GlobalTabT extends HTMLElement {
    connectedCallback() {
      this._onTabSelect = this._handleTabSelect.bind(this);
      this.addEventListener('tab:select', this._onTabSelect);

      // 初始化：确保正确的面板显示
      requestAnimationFrame(() => {
        this._initializePanels();
      });
    }

    disconnectedCallback() {
      this.removeEventListener('tab:select', this._onTabSelect);
    }

    // 初始化面板显示状态
    _initializePanels() {
      const activeLevelOne = this.querySelector('tab-list[level="one"] tab-item[active]');
      if (activeLevelOne) {
        const tabId = activeLevelOne.dataset.tabId;
        this._updateLevelTwo(tabId);
      }
    }

    // 处理tab选择
    _handleTabSelect(event) {
      const { tabId, level, parentId } = event.detail;

      if (level === 'one') {
        this._handleLevelOneSelect(tabId);
      } else if (level === 'two') {
        this._handleLevelTwoSelect(tabId);
      }
    }

    // 处理一级tab选择
    _handleLevelOneSelect(tabId) {
      this._updateLevelTwo(tabId);
    }

    // 更新二级tab显示
    _updateLevelTwo(levelOneId) {
      const allLevelTwo = this.querySelectorAll('tab-list[level="two"]');
      let hasVisibleLevelTwo = false;

      allLevelTwo.forEach(list => {
        const shouldShow = list.dataset.parent === levelOneId;
        list.hidden = !shouldShow;

        if (shouldShow) {
          hasVisibleLevelTwo = true;
          
          // 检查是否有已激活的tab
          let activeTab = list.querySelector('tab-item[active]');
          
          // 如果没有激活的tab，激活第一个
          if (!activeTab) {
            const firstTab = list.querySelector('tab-item');
            if (firstTab) {
              // 移除所有激活状态
              list.querySelectorAll('tab-item[active]').forEach(tab => {
                tab.removeAttribute('active');
              });
              firstTab.setAttribute('active', '');
              activeTab = firstTab;
            }
          }

          // 显示对应的面板，滚动到第一个位置
          if (activeTab) {
            requestAnimationFrame(() => {
              list.scrollToStart();
              list.updateIndicator();
              this._showPanel(activeTab.dataset.tabId);
            });
          }
        }
      });

      // 如果没有二级tab，直接显示一级对应的面板
      if (!hasVisibleLevelTwo) {
        this._showPanel(levelOneId);
      }
    }

    // 处理二级tab选择
    _handleLevelTwoSelect(tabId) {
      this._showPanel(tabId);
    }

    // 显示指定面板
    _showPanel(tabId) {
      const allPanels = this.querySelectorAll('tab-panel');
      allPanels.forEach(panel => {
        panel.hidden = panel.dataset.tabId !== tabId;
      });
    }

  }

  // 注册自定义元素
  if (!customElements.get('tab-list')) {
    customElements.define('tab-list', TabListT);
  }
  if (!customElements.get('tab-item')) {
    customElements.define('tab-item', TabItemT);
  }
  if (!customElements.get('tab-panel')) {
    customElements.define('tab-panel', TabPanelT);
  }
  if (!customElements.get('global-tab')) {
    customElements.define('global-tab', GlobalTabT);
  }

})();
